/**
 * FileManager Service
 *
 * API service for file and folder management operations.
 */

import axiosInstance from "@/axiosConfig";
import { postFile } from "@/shared/services/directUploads.service";
import { getApiUrl } from "@/shared/utils/api";
import { FILE_MANAGER_ENDPOINTS } from "../configs/endpoints";
import type {
  FolderResponse,
  EntryResponse,
  FolderWithContentsResponse,
  FoldersListResponse,
  EntriesListResponse,
  NodesListResponse,
  EntryDetailsResponse,
  CreateFolderPayload,
  UpdateFolderPayload,
  CreateEntryPayload,
  UpdateEntryPayload,
  FolderQueryParams,
  EntriesQueryParams,
  NodesQueryParams,
} from "./file-manager.service.types";

// Re-export types for convenience
export type {
  FolderResponse,
  EntryResponse,
  SubfolderResponse,
  SubentryResponse,
  FolderWithContentsResponse,
  PaginationResponse,
  FoldersListResponse,
  EntriesListResponse,
  NodesListResponse,
  EntryDetailsResponse,
  CreateFolderPayload,
  UpdateFolderPayload,
  CreateEntryPayload,
  UpdateEntryPayload,
  FolderQueryParams,
  EntriesQueryParams,
  NodesQueryParams,
} from "./file-manager.service.types";

export class FileManagerService {
  // ============================================================================
  // FOLDER MANAGEMENT
  // ============================================================================

  /**
   * Create a new folder
   * @param payload - Folder creation data
   * @returns Promise<FolderResponse>
   */
  static async createFolder(payload: CreateFolderPayload): Promise<FolderResponse> {
    const response = await axiosInstance.post<FolderResponse>(
      getApiUrl(FILE_MANAGER_ENDPOINTS.CREATE_FOLDER),
      payload,
    );
    return response.data;
  }

  /**
   * List folders with optional filtering by parent
   * @param params - Query parameters for filtering and pagination
   * @returns Promise<FoldersListResponse>
   */
  static async listFolders(params: FolderQueryParams = {}): Promise<FoldersListResponse> {
    const response = await axiosInstance.get<FoldersListResponse>(
      getApiUrl(FILE_MANAGER_ENDPOINTS.LIST_FOLDERS),
      { params },
    );
    return response.data;
  }

  /**
   * List root folders (folders without parent)
   * @param params - Query parameters for pagination
   * @returns Promise<FoldersListResponse>
   */
  static async listRootFolders(params: Omit<FolderQueryParams, "parent_id"> = {}): Promise<FoldersListResponse> {
    const response = await axiosInstance.get<FoldersListResponse>(
      getApiUrl(FILE_MANAGER_ENDPOINTS.LIST_ROOT_FOLDERS),
      { params },
    );
    return response.data;
  }

  /**
   * List all nodes (folders and entries) with parent information
   * @param params - Query parameters for pagination and search
   * @returns Promise<NodesListResponse>
   */
  static async listAllNodes(params: NodesQueryParams = {}): Promise<NodesListResponse> {
    const response = await axiosInstance.get<NodesListResponse>(
      getApiUrl(FILE_MANAGER_ENDPOINTS.LIST_ALL_NODES),
      { params },
    );
    return response.data;
  }

  /**
   * Navigate to folder by path
   * @param path - Folder path (e.g., "Documents/2024/January")
   * @returns Promise<FolderWithContentsResponse>
   */
  static async navigateToFolderByPath(path: string): Promise<FolderWithContentsResponse> {
    const response = await axiosInstance.get<FolderWithContentsResponse>(
      getApiUrl(FILE_MANAGER_ENDPOINTS.NAVIGATE_TO_FOLDER),
      { params: { path } },
    );
    return response.data;
  }

  /**
   * Get folder details including subfolders and entries
   * @param folderId - Folder ID
   * @returns Promise<FolderWithContentsResponse>
   */
  static async getFolderDetails(folderId: string | number): Promise<FolderWithContentsResponse> {
    const response = await axiosInstance.get<FolderWithContentsResponse>(
      getApiUrl(FILE_MANAGER_ENDPOINTS.GET_FOLDER(folderId)),
    );
    return response.data;
  }

  /**
   * Counts everything a deletion would take along: the subfolders and files of the given
   * folders, at every depth. Walks the tree level by level through the folder details, which
   * only list live nodes (the API's subfolders_count / entries_count also count erased ones).
   */
  static async countFolderContents(folderIds: string[]): Promise<{ folders: number, files: number }> {
    const counts = { folders: 0, files: 0 };
    const visited = new Set(folderIds);
    let level = folderIds;

    while (level.length > 0) {
      const details = await Promise.all(level.map((id) => FileManagerService.getFolderDetails(id)));
      level = [];
      for (const { folder } of details) {
        counts.folders += folder.subfolders?.length ?? 0;
        counts.files += folder.entries?.length ?? 0;
        for (const subfolder of folder.subfolders ?? []) {
          const id = String(subfolder.id);
          if (!visited.has(id)) {
            visited.add(id);
            level.push(id);
          }
        }
      }
    }
    return counts;
  }

  /**
   * Update folder properties
   * @param folderId - Folder ID
   * @param payload - Update data
   * @returns Promise<FolderResponse>
   */
  static async updateFolder(folderId: string | number, payload: UpdateFolderPayload): Promise<FolderResponse> {
    const response = await axiosInstance.put<FolderResponse>(
      getApiUrl(FILE_MANAGER_ENDPOINTS.UPDATE_FOLDER(folderId)),
      payload,
    );
    return response.data;
  }

  /**
   * Delete a folder (must be empty)
   * @param folderId - Folder ID
   * @returns Promise<void>
   */
  static async deleteFolder(folderId: string | number): Promise<void> {
    await axiosInstance.delete(getApiUrl(FILE_MANAGER_ENDPOINTS.DELETE_FOLDER(folderId)));
  }

  // ============================================================================
  // ENTRY MANAGEMENT
  // ============================================================================

  /**
   * Create a new entry (file) in a folder
   * @param folderId - Parent folder ID
   * @param payload - Entry creation data
   * @returns Promise<EntryResponse>
   */
  static async createEntry(folderId: string | number, payload: CreateEntryPayload): Promise<EntryResponse> {
    const response = await axiosInstance.post<EntryResponse>(
      getApiUrl(FILE_MANAGER_ENDPOINTS.CREATE_ENTRY(folderId)),
      payload,
    );
    return response.data;
  }

  /**
   * List entries in a folder
   * @param folderId - Folder ID
   * @param params - Query parameters for pagination
   * @returns Promise<EntriesListResponse>
   */
  static async listEntries(folderId: string | number, params: EntriesQueryParams = {}): Promise<EntriesListResponse> {
    const response = await axiosInstance.get<EntriesListResponse>(
      getApiUrl(FILE_MANAGER_ENDPOINTS.LIST_ENTRIES(folderId)),
      { params },
    );
    return response.data;
  }

  /**
   * Get entry details
   * @param folderId - Parent folder ID
   * @param entryId - Entry ID
   * @returns Promise<EntryDetailsResponse>
   */
  static async getEntryDetails(folderId: string | number, entryId: string | number): Promise<EntryDetailsResponse> {
    const response = await axiosInstance.get<EntryDetailsResponse>(
      getApiUrl(FILE_MANAGER_ENDPOINTS.GET_ENTRY(folderId, entryId)),
    );
    return response.data;
  }

  /**
   * Update entry properties
   * @param folderId - Parent folder ID
   * @param entryId - Entry ID
   * @param payload - Update data
   * @returns Promise<EntryResponse>
   */
  static async updateEntry(
    folderId: string | number,
    entryId: string | number,
    payload: UpdateEntryPayload,
  ): Promise<EntryResponse> {
    const response = await axiosInstance.put<EntryResponse>(
      getApiUrl(FILE_MANAGER_ENDPOINTS.UPDATE_ENTRY(folderId, entryId)),
      payload,
    );
    return response.data;
  }

  /**
   * Delete an entry
   * @param folderId - Parent folder ID
   * @param entryId - Entry ID
   * @returns Promise<void>
   */
  static async deleteEntry(folderId: string | number, entryId: string | number): Promise<void> {
    await axiosInstance.delete(getApiUrl(FILE_MANAGER_ENDPOINTS.DELETE_ENTRY(folderId, entryId)));
  }

  // ============================================================================
  // FILE UPLOAD INTEGRATION
  // ============================================================================

  /**
   * Upload a file and create an entry
   * @param file - File to upload
   * @param folderId - Parent folder ID
   * @param directUploadUrl - Optional direct upload URL from folder data
   * @returns Promise<EntryResponse>
   */
  static async uploadFile(file: File, folderId: string | number, directUploadUrl?: string): Promise<EntryResponse> {
    // First upload the file to get the signed_id
    const uploadResult = await postFile(file, directUploadUrl);

    // Then create the entry with the signed_id
    const payload: CreateEntryPayload = {
      entry: {
        name: file.name,
        document: uploadResult.signedId,
      },
    };

    return this.createEntry(folderId, payload);
  }

  /**
   * Upload multiple files and create entries
   * @param files - Files to upload
   * @param folderId - Parent folder ID
   * @param directUploadUrl - Optional direct upload URL from folder data
   * @returns Promise<EntryResponse[]>
   */
  static async uploadFiles(
    files: File[],
    folderId: string | number,
    directUploadUrl?: string,
  ): Promise<EntryResponse[]> {
    const uploadPromises = files.map((file) => this.uploadFile(file, folderId, directUploadUrl));
    return Promise.all(uploadPromises);
  }
}

// Export individual functions for flexible usage
export const {
  createFolder,
  listFolders,
  listRootFolders,
  listAllNodes,
  navigateToFolderByPath,
  getFolderDetails,
  updateFolder,
  deleteFolder,
  createEntry,
  listEntries,
  getEntryDetails,
  updateEntry,
  deleteEntry,
  uploadFile,
  uploadFiles,
} = FileManagerService;
